/** Maximum number of intermediate stops (waypoints) allowed per route */
export const MAX_WAYPOINTS = 10

/**
 * National average fuel prices — fallback of last resort.
 *
 * Only fires when: (a) EIA is unreachable AND (b) we have no cached data
 * (typical on a fresh container whose first EIA call failed). Otherwise
 * the pipeline prefers cached state-level prices or stale cache.
 *
 * Values: unweighted mean across 51 EIA state entries as of 2026-04-17.
 * Intentionally rounded slightly conservative (tour managers would rather
 * over-budget than under-budget when they see a "using fallback" banner).
 *
 * **Review annually** — fuel prices drift. A multi-year-stale fallback
 * could produce materially wrong estimates during an EIA outage. Last
 * deep update: 2026-04-17. Previous values ($3.50/$3.85) were ~49% low
 * on diesel by this date.
 */
export const NATIONAL_AVERAGE_PRICES = {
	gas: 4.1,
	diesel: 5.75,
} as const

/** EIA API endpoint for fuel price data */
export const EIA_API_BASE = 'https://api.eia.gov/v2/petroleum/pri/gnd/data/'

/** Mapbox API base URL */
export const MAPBOX_API_BASE = 'https://api.mapbox.com'

/**
 * Elevation sampling interval in miles.
 *
 * Tuning history:
 *   - 3 miles (initial): high fidelity but a cross-country route produced
 *     ~700 samples = 2 Open-Meteo batches. Even with a 750ms gap between
 *     batches, the sustained burst tripped Open-Meteo's per-IP rate
 *     limit (shared Render egress IPs make this worse). Users saw 429
 *     → Mapbox fallback on every long route.
 *   - 5 miles (current): drops a 2,100-mile route to ~420 samples,
 *     fitting in a SINGLE 500-coord Open-Meteo request. No batch gap,
 *     no burst, no 429. Accuracy impact < 1% of total fuel cost —
 *     mountain passes (typically 10-30 miles of climb) still get 2-6
 *     sample points across them.
 *
 * If we ever move elevation to a paid commercial Open-Meteo plan or
 * pure Mapbox elevation, this can drop back to 3 miles for higher
 * fidelity. Until then, 5 is the last value where we still measure
 * mountain passes properly without bursting the rate limit.
 */
export const ELEVATION_SAMPLE_INTERVAL_MILES = 5

// ─── Grade → MPG penalty coefficients ──────────────────────────────────────
//
// These four constants (HEAVY, LIGHT, RECOVERY, FLOOR/CEILING clamps) form
// our elevation-adjusted MPG model. Calibrated against two US national-lab
// empirical studies — full derivation in docs/FUEL_APP.md § Grade Model
// Calibration & Sources.
//
//   [NREL-2015] Lopp, Wood & Duran, "Evaluating the Impact of Road Grade on
//     Simulated Commercial Vehicle Fuel Economy Using Real-World Drive
//     Cycles," SAE 2015-01-2739 / NREL/CP-5400-64544, Sept 2015.
//     https://docs.nrel.gov/docs/fy16osti/64544.pdf
//     125,469 miles of real-world drive cycles simulated in FASTSim across
//     bus, parcel, refuse, and beverage-delivery vocations. Primary source
//     for the heavy-vehicle coefficient and the asymmetric descent recovery.
//
//   [ORNL-2011] Franzese & Davidson, "Effect of Weight and Roadway Grade on
//     the Fuel Economy of Class-8 Freight Trucks," ORNL/TM-2011/471, Oct 2011.
//     https://info.ornl.gov/sites/publications/files/Pub33386.pdf
//     ~700,000 miles of real-world Class-8 telemetry at 5 Hz — the
//     foundational dataset NREL's 2015 simulation work builds on.
//
// ───────────────────────────────────────────────────────────────────────────

/**
 * Grade penalty factor for heavy vehicles (buses, trucks).
 * MPG decreases by this factor per 1% grade uphill.
 * Example: 5% grade on a heavy vehicle = 5 * 0.05 = 25% MPG reduction.
 *
 * Calibration: [NREL-2015] Table 4 shows bus vocation at 2.8% RMS grade
 * incurs 9.0% average fuel-use penalty, with trip-specific penalties
 * reaching 40%+ at 1% average grade (Figure 4). Our 5% coefficient
 * produces ~16% uphill fuel penalty at 2.8% grade per mile — on the
 * aggressive end of their vocation range (4-15% at RMS ~3%), which is
 * the right bias for a tour-budget estimator (over-budget beats under-
 * budget).  [ORNL-2011] provides the empirical Class-8 baseline.
 */
export const GRADE_FACTOR_HEAVY = 0.05

/**
 * Grade penalty factor for light vehicles (sedans, SUVs).
 * Less affected by grades due to lower weight.
 *
 * Calibration: [NREL-2015] Table 4 reports 0.8-1.4% fuel penalty at
 * ~1.2-1.7% RMS grade for parcel and beverage-delivery vocations — the
 * lightest vehicles in their dataset. Passenger cars weigh less still,
 * but published per-1%-grade coefficients for sedans fall in the 2-3%
 * range; we pick 3% to stay consistent with the conservative framing
 * of the heavy factor.
 */
export const GRADE_FACTOR_LIGHT = 0.03

/**
 * Recovery factor for downhill grades.
 * Much lower than uphill penalty — engine braking limits fuel savings.
 *
 * Calibration: [NREL-2015] states explicitly: "On average the penalty
 * to ascend exceeds the benefit of descent so that trips with net-zero
 * elevation but significant RMS grade can see fuel use increases of
 * up to 40%." Our 2.5× asymmetry (HEAVY=0.05 vs RECOVERY=0.02) encodes
 * this finding — ascents dominate; recoveries never fully cancel them.
 */
export const GRADE_RECOVERY_FACTOR = 0.02

/**
 * MPG floor — worst case, can't go below 40% of base MPG.
 *
 * Grade-vs-fuel response is only approximately linear up to ~5% grade
 * in both source studies. Beyond that, engines downshift and the
 * linear extrapolation over-penalizes. The floor keeps the model
 * bounded in its non-linear regime. [NREL-2015] notes penalties can
 * exceed 30% at RMS grade above 6%, which our clamp accommodates.
 */
export const MPG_FLOOR_RATIO = 0.4

/**
 * MPG ceiling — downhill benefit capped at 150% of base MPG.
 *
 * Physical upper bound: even on long continuous descent, engine fuel
 * idle + accessory loads + safety-speed throttling prevent unbounded
 * MPG improvement. Both source studies treat descents conservatively
 * given engine-braking realities.
 */
export const MPG_CEILING_RATIO = 1.5

/**
 * States for which EIA publishes **direct per-state** weekly fuel prices.
 *
 * For every other state, EIA only publishes a PADD (Petroleum Administration
 * for Defense Districts) regional average — so every state in the same PADD
 * bucket shares an identical price in our data. The 41 non-direct states are
 * not individually measured; they're getting their PADD regional proxy.
 *
 * This Set is the client-safe source of truth for "is this price a real state
 * measurement or a regional proxy?". Used by the ticker and state breakdown
 * to honestly label each figure with its data source. Kept in sync with
 * DIRECT_STATE_AREAS in `fuel-prices-cache.ts` — the `S**` prefix there is
 * the EIA series-area code convention, same 9 states.
 *
 * Authoritative list as of 2026-04: CA, CO, FL, MA, MN, NY, OH, TX, WA.
 */
export const DIRECT_STATE_CODES: ReadonlySet<string> = new Set([
	'CA',
	'CO',
	'FL',
	'MA',
	'MN',
	'NY',
	'OH',
	'TX',
	'WA',
])

/**
 * Human label for a state price's data source. Used next to a price to
 * explain, at a glance, how granular the number is.
 * - `state`: EIA publishes this state individually; most accurate.
 * - `PADD`:  Regional average; every state in the PADD bucket shares it.
 */
export function priceSourceLabel(stateCode: string): 'state' | 'PADD' {
	return DIRECT_STATE_CODES.has(stateCode) ? 'state' : 'PADD'
}

/**
 * The synthetic "state" code for route miles that fall outside every U.S.
 * state polygon — Canada/Mexico border crossings and coastal-water clipping.
 * These miles carry no per-state EIA price, so they're charged the national
 * average. Kept as a named constant so the display layer can detect the bucket
 * without magic-stringing `'US'` in three components.
 */
export const NON_US_BUCKET_CODE = 'US'

/**
 * Display name for a per-state breakdown row. The off-map bucket renders as
 * "Non-U.S. (avg)" so a big Canada crossing never shows up as a cryptic bare
 * "US" row sitting next to real states like NY and MI (it was reading as a
 * mystery bucket — see the Syracuse→Detroit route, 45% of which crosses
 * Ontario). Real states fall through to their full name.
 */
export function stateDisplayName(stateCode: string): string {
	if (stateCode === NON_US_BUCKET_CODE) return 'Non-U.S. (avg)'
	return STATE_NAMES[stateCode] ?? stateCode
}

/** US state code to full name mapping */
export const STATE_NAMES: Record<string, string> = {
	AL: 'Alabama',
	AK: 'Alaska',
	AZ: 'Arizona',
	AR: 'Arkansas',
	CA: 'California',
	CO: 'Colorado',
	CT: 'Connecticut',
	DE: 'Delaware',
	FL: 'Florida',
	GA: 'Georgia',
	HI: 'Hawaii',
	ID: 'Idaho',
	IL: 'Illinois',
	IN: 'Indiana',
	IA: 'Iowa',
	KS: 'Kansas',
	KY: 'Kentucky',
	LA: 'Louisiana',
	ME: 'Maine',
	MD: 'Maryland',
	MA: 'Massachusetts',
	MI: 'Michigan',
	MN: 'Minnesota',
	MS: 'Mississippi',
	MO: 'Missouri',
	MT: 'Montana',
	NE: 'Nebraska',
	NV: 'Nevada',
	NH: 'New Hampshire',
	NJ: 'New Jersey',
	NM: 'New Mexico',
	NY: 'New York',
	NC: 'North Carolina',
	ND: 'North Dakota',
	OH: 'Ohio',
	OK: 'Oklahoma',
	OR: 'Oregon',
	PA: 'Pennsylvania',
	RI: 'Rhode Island',
	SC: 'South Carolina',
	SD: 'South Dakota',
	TN: 'Tennessee',
	TX: 'Texas',
	UT: 'Utah',
	VT: 'Vermont',
	VA: 'Virginia',
	WA: 'Washington',
	WV: 'West Virginia',
	WI: 'Wisconsin',
	WY: 'Wyoming',
	DC: 'District of Columbia',
}

/** EIA PADD region codes by state — used to query regional prices when state-level unavailable */
export const STATE_TO_EIA_CODE: Record<string, string> = {
	AL: 'SAL',
	AK: 'SAK',
	AZ: 'SAZ',
	AR: 'SAR',
	CA: 'SCA',
	CO: 'SCO',
	CT: 'SCT',
	DE: 'SDE',
	FL: 'SFL',
	GA: 'SGA',
	HI: 'SHI',
	ID: 'SID',
	IL: 'SIL',
	IN: 'SIN',
	IA: 'SIA',
	KS: 'SKS',
	KY: 'SKY',
	LA: 'SLA',
	ME: 'SME',
	MD: 'SMD',
	MA: 'SMA',
	MI: 'SMI',
	MN: 'SMN',
	MS: 'SMS',
	MO: 'SMO',
	MT: 'SMT',
	NE: 'SNE',
	NV: 'SNV',
	NH: 'SNH',
	NJ: 'SNJ',
	NM: 'SNM',
	NY: 'SNY',
	NC: 'SNC',
	ND: 'SND',
	OH: 'SOH',
	OK: 'SOK',
	OR: 'SOR',
	PA: 'SPA',
	RI: 'SRI',
	SC: 'SSC',
	SD: 'SSD',
	TN: 'STN',
	TX: 'STX',
	UT: 'SUT',
	VT: 'SVT',
	VA: 'SVA',
	WA: 'SWA',
	WV: 'SWV',
	WI: 'SWI',
	WY: 'SWY',
	DC: 'SDC',
}

/**
 * Stripe Payment Link for donations (CLB Consulting live account, created
 * 2026-10-05): donor-chosen amount, $10 preset, $3–$500, "Donate" button,
 * metadata `app: fuel-estimator`. A public URL, not a secret — opening it is
 * a plain link, no Stripe.js and no CSP change.
 */
export const DONATE_URL = 'https://donate.stripe.com/cNi5kC3Sn9qpa8Nbbqf3a02'
